import React, { useState, useRef, useEffect, useCallback } from 'react'
import { getKey, setKey, hasKey } from 'api/apiKey'
import { changePrompt } from 'actions/prompt'
import { requestContentGeneration, closeContentGenerationModal, DEFAULT_CONTENT_GENERATION_MODE } from 'actions/contentGeneration'
import type { ContentGenerationMode } from 'actions/contentGeneration'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import selectPrompt from 'selectors/prompt'
import selectContentGeneration from 'selectors/contentGeneration'
import Button from 'components/utils/Button'
import Modal from 'components/utils/Modal'
import FormControl from 'components/utils/FormControl'
import 'components/ContentGenerationModal.less'

type InputEventData = {
  target?: {
    value?: string
  }
}

type KeyboardEventData = {
  key?: string
  shiftKey?: boolean
  preventDefault?: () => void
  stopPropagation?: () => void
}

type ContentGenerationData = {
  isFetching?: boolean
  isFailed?: boolean
  showModal?: boolean
  status?: string
  lastMode?: ContentGenerationMode
}

const ContentGenerationModal = () => {
  const dispatch = useAppDispatch()
  const prompt = useAppSelector((state) => selectPrompt(state as any) as string)
  const contentGeneration = useAppSelector((state) => selectContentGeneration(state as any) as ContentGenerationData)
  const lastMode: ContentGenerationMode = contentGeneration?.lastMode || DEFAULT_CONTENT_GENERATION_MODE

  const [apiKey, setApiKey] = useState('')
  const keyInputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null)
  const promptInputRef = useRef<HTMLTextAreaElement | null>(null)
  const prevShowModal = useRef(false)

  useEffect(() => {
    const wasClosedNowOpen = !prevShowModal.current && !!contentGeneration?.showModal
    prevShowModal.current = !!contentGeneration?.showModal

    if (wasClosedNowOpen) {
      if (hasKey()) {
        setTimeout(() => promptInputRef.current?.focus?.(), 0)
      } else {
        setTimeout(() => keyInputRef.current?.focus?.(), 0)
      }
    }
  }, [contentGeneration?.showModal])

  const closeGenerateModal = useCallback(() => {
    dispatch(closeContentGenerationModal())
  }, [dispatch])

  const ensureApiKey = useCallback(() => {
    if (hasKey()) {
      return true
    }
    setKey(apiKey)
    if (!apiKey?.trim()?.length) {
      keyInputRef.current?.focus?.()
      return false
    }
    return true
  }, [apiKey])

  const confirmGenerate = useCallback(() => {
    if (contentGeneration?.isFetching) return
    if (!ensureApiKey()) {
      return
    }
    dispatch(requestContentGeneration('generate'))
  }, [contentGeneration?.isFetching, ensureApiKey, dispatch])

  const confirmConvert = useCallback(() => {
    if (contentGeneration?.isFetching) return
    if (!ensureApiKey()) {
      return
    }
    dispatch(requestContentGeneration('convert'))
  }, [contentGeneration?.isFetching, ensureApiKey, dispatch])

  const confirmEdit = useCallback(() => {
    if (contentGeneration?.isFetching) return
    if (!ensureApiKey()) {
      return
    }
    dispatch(requestContentGeneration('edit'))
  }, [contentGeneration?.isFetching, ensureApiKey, dispatch])

  const confirmForMode = useCallback((mode: ContentGenerationMode) => {
    if (mode === 'generate') {
      confirmGenerate()
      return
    }
    if (mode === 'edit') {
      confirmEdit()
      return
    }
    confirmConvert()
  }, [confirmConvert, confirmEdit, confirmGenerate])

  return (
    <Modal
      open={!!contentGeneration?.showModal}
      onClose={closeGenerateModal}
      className="content-generation-modal"
    >
      <div className="modal-body">
        {!hasKey() ? (
          <div>
            <FormControl
              type="password"
              value={apiKey || ''}
              onChange={(e: unknown) => {
                const value = ((e as InputEventData).target?.value || '')
                setApiKey(value)
              }}
              placeholder="Anthropic API key"
              disabled={contentGeneration?.isFetching}
              ref={(el: HTMLInputElement | null) => (keyInputRef.current = el)}
              name="anthropic_api_key"
              autoComplete="current-password"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              onKeyDown={(e: unknown) => {
                const ke = e as KeyboardEventData
                if (ke.key === 'Escape') {
                  closeGenerateModal()
                }
                if (ke.key === 'Enter') {
                  ke.preventDefault?.()
                  ke.stopPropagation?.()
                }
              }}
            />
            <div style={{ height: '0.75em' }} />
          </div>
        ) : null}
        <FormControl
          as="textarea"
          value={prompt}
          onChange={(e: unknown) => dispatch(changePrompt((e as InputEventData).target?.value || ''))}
          placeholder="What to explore..."
          disabled={contentGeneration?.isFetching}
          rows={4}
          ref={(el: HTMLTextAreaElement | null) => (promptInputRef.current = el)}
          onKeyDown={(e: unknown) => {
            const ke = e as KeyboardEventData
            if (ke.key === 'Escape') {
              closeGenerateModal()
            }
            if (ke.key === 'Enter' && !ke.shiftKey) {
              ke.preventDefault?.()
              if (prompt.length === 0) {
                confirmGenerate()
              } else {
                confirmForMode(lastMode)
              }
            }
          }}
        />
      </div>
      <div className="modal-footer">
        <Button variant="primary" onClick={confirmGenerate} disabled={contentGeneration?.isFetching}>
          Generate
        </Button>
        <Button
          variant="primary"
          onClick={confirmConvert}
          disabled={contentGeneration?.isFetching || prompt.length === 0}
        >
          Convert
        </Button>
        <Button
          variant="primary"
          onClick={confirmEdit}
          disabled={contentGeneration?.isFetching || prompt.length === 0}
        >
          Edit
        </Button>

        <Button onClick={closeGenerateModal} disabled={contentGeneration?.isFetching}>
          Cancel
        </Button>
      </div>
    </Modal>
  )
}

export default ContentGenerationModal
